
import toast from 'react-hot-toast'

// Função para exportar contrato em PDF
export const exportContrateToPDF = (contrato: any) => {
  try {
    // Criar conteúdo HTML do contrato
    const htmlContent = generateContratoHTML(contrato)
    
    // Abrir nova janela para impressão/PDF
    const printWindow = window.open('', '_blank')
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Contrato ${contrato.numero}</title>
          <meta charset="utf-8">
          <style>
            body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
            .section { margin-bottom: 25px; }
            .section h3 { color: #333; border-bottom: 1px solid #ccc; padding-bottom: 5px; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
            .info-item { margin-bottom: 10px; }
            .info-label { font-weight: bold; color: #555; }
            .signature-area { margin-top: 50px; display: grid; grid-template-columns: 1fr 1fr; gap: 50px; }
            .signature-box { border-top: 1px solid #333; padding-top: 10px; text-align: center; }
            .etapas-table { width: 100%; border-collapse: collapse; margin-top: 15px; }
            .etapas-table th, .etapas-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            .etapas-table th { background-color: #f5f5f5; }
            @media print { body { margin: 20px; } }
          </style>
        </head>
        <body>
          ${htmlContent}
        </body>
        </html>
      `)
      printWindow.document.close()
      
      // Aguardar carregamento e abrir diálogo de impressão
      setTimeout(() => {
        printWindow.print()
      }, 250)
      
      toast.success('PDF gerado com sucesso!')
    }
  } catch (error) {
    console.error('Erro ao gerar PDF:', error)
    toast.error('Erro ao gerar PDF')
  }
}

// Função para exportar contrato em Word
export const exportContratoToWord = (contrato: any) => {
  try {
    // Gerar conteúdo HTML do contrato
    const htmlContent = generateContratoHTML(contrato)
    
    // Criar blob com conteúdo HTML (Word pode abrir HTML)
    const blob = new Blob([`
      <!DOCTYPE html>
      <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word'>
      <head>
        <meta charset='utf-8'>
        <title>Contrato ${contrato.numero}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
          .section { margin-bottom: 25px; }
          .section h3 { color: #333; border-bottom: 1px solid #ccc; padding-bottom: 5px; }
          .info-grid { margin-bottom: 20px; }
          .info-item { margin-bottom: 10px; }
          .info-label { font-weight: bold; color: #555; }
          .signature-area { margin-top: 50px; }
          .signature-box { border-top: 1px solid #333; padding-top: 10px; text-align: center; margin-bottom: 30px; }
          .etapas-table { width: 100%; border-collapse: collapse; margin-top: 15px; }
          .etapas-table th, .etapas-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          .etapas-table th { background-color: #f5f5f5; }
        </style>
      </head>
      <body>
        ${htmlContent}
      </body>
      </html>
    `], { type: 'application/msword' })
    
    // Criar link para download
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `Contrato_${contrato.numero}_${contrato.nomeCliente.replace(/\s+/g, '_')}.doc`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    
    toast.success('Arquivo Word gerado com sucesso!')
  } catch (error) {
    console.error('Erro ao gerar Word:', error)
    toast.error('Erro ao gerar arquivo Word')
  }
}

// Função para gerar HTML do contrato
const generateContratoHTML = (contrato: any) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'em_desenvolvimento': return 'Em Desenvolvimento'
      case 'enviado': return 'Enviado'
      case 'aprovado': return 'Aprovado'
      case 'perdido': return 'Perdido'
      default: return status
    }
  }

  return `
    <div class="header">
      <h1>CONTRATO DE PRESTAÇÃO DE SERVIÇOS</h1>
      <h2>Nº ${contrato.numero}</h2>
    </div>

    <div class="section">
      <h3>DADOS DO CONTRATO</h3>
      <div class="info-grid">
        <div>
          <div class="info-item">
            <span class="info-label">Data de Emissão:</span> ${formatDate(contrato.dataEmissao)}
          </div>
          <div class="info-item">
            <span class="info-label">Data de Validade:</span> ${formatDate(contrato.dataValidade)}
          </div>
          <div class="info-item">
            <span class="info-label">Status:</span> ${getStatusLabel(contrato.status)}
          </div>
        </div>
        <div>
          <div class="info-item">
            <span class="info-label">Valor Total:</span> ${formatCurrency(contrato.valorTotal)}
          </div>
          ${contrato.dataFechamento ? `
          <div class="info-item">
            <span class="info-label">Data de Fechamento:</span> ${formatDate(contrato.dataFechamento)}
          </div>
          ` : ''}
        </div>
      </div>
    </div>

    <div class="section">
      <h3>PARTES CONTRATANTES</h3>
      <div class="info-grid">
        <div>
          <h4>CONTRATANTE</h4>
          <div class="info-item">
            <span class="info-label">Nome:</span> ${contrato.nomeCliente}
          </div>
          ${contrato.contatoCliente ? `
          <div class="info-item">
            <span class="info-label">Contato:</span> ${contrato.contatoCliente}
          </div>
          ` : ''}
        </div>
        <div>
          <h4>CONTRATADA</h4>
          <div class="info-item">
            <span class="info-label">Empresa:</span> [NOME_DA_EMPRESA]
          </div>
          <div class="info-item">
            <span class="info-label">CNPJ:</span> [CNPJ_DA_EMPRESA]
          </div>
        </div>
      </div>
    </div>

    <div class="section">
      <h3>OBJETO DO CONTRATO</h3>
      <div class="info-item">
        <span class="info-label">Projeto:</span> ${contrato.nomeProjeto}
      </div>
      <div class="info-item">
        <span class="info-label">Descrição:</span>
        <p>${contrato.descricaoProjeto}</p>
      </div>
    </div>

    ${contrato.etapas && contrato.etapas.length > 0 ? `
    <div class="section">
      <h3>ETAPAS DO PROJETO</h3>
      <table class="etapas-table">
        <thead>
          <tr>
            <th>Código</th>
            <th>Etapa</th>
            <th>Descrição</th>
          </tr>
        </thead>
        <tbody>
          ${contrato.etapas.map(etapa => `
            <tr>
              <td>${etapa.codigo}</td>
              <td>${etapa.nome}</td>
              <td>${etapa.subetapas ? etapa.subetapas.map(sub => sub.nome).join(', ') : ''}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
    ` : ''}

    ${contrato.clausulasEspeciais ? `
    <div class="section">
      <h3>CLÁUSULAS ESPECIAIS</h3>
      <p>${contrato.clausulasEspeciais}</p>
    </div>
    ` : ''}

    ${contrato.condicoesGerais ? `
    <div class="section">
      <h3>CONDIÇÕES GERAIS</h3>
      <p>${contrato.condicoesGerais}</p>
    </div>
    ` : ''}

    ${contrato.observacoes ? `
    <div class="section">
      <h3>OBSERVAÇÕES</h3>
      <p>${contrato.observacoes}</p>
    </div>
    ` : ''}

    <div class="signature-area">
      <div class="signature-box">
        <p><strong>CONTRATADA</strong></p>
        <br><br>
        <p>${contrato.assinaturaEmpresa}</p>
      </div>
      <div class="signature-box">
        <p><strong>CONTRATANTE</strong></p>
        <br><br>
        <p>${contrato.assinaturaCliente}</p>
      </div>
    </div>

    <div style="margin-top: 50px; text-align: center; font-size: 12px; color: #666;">
      <p>Documento gerado em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}</p>
    </div>
  `
}
